import React, { useState, useEffect, useCallback, useRef } from "react";
import { Routes, Route, useLocation, useNavigate, useParams } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import { auth } from "./firebase";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { ToastProvider } from "./components/Toast";
import { DialogProvider } from "./components/AppDialog";
import useSessionTimeout from "./hooks/useSessionTimeout";
import ActivarCuenta from "./screens/ActivarCuenta";
import ActivacionExpirada from "./screens/ActivacionExpirada";
import AsambleaPublica from "./screens/AsambleaPublica";
import EntrarAsamblea from "./screens/EntrarAsamblea";
import CotizacionPrint from "./screens/CotizacionPrint";
import SolicitarDemo from "./screens/SolicitarDemo";
import DemoBanner from "./components/DemoBanner";
import Header from "./components/Header";
import HeaderAdmin from "./components/HeaderAdmin";
import HeaderSuperAdmin from "./components/HeaderSuperAdmin";
import Sidebar from "./components/Sidebar";
import SidebarAdmin from "./components/SidebarAdmin";
import SidebarSuperAdmin from "./components/SidebarSuperAdmin";
import SidebarComercial from "./components/SidebarComercial";
import HeaderComercial from "./components/HeaderComercial";
import HomeComercial from "./screens/comercial/HomeComercial";
import MapaProspeccion from "./screens/comercial/MapaProspeccion";
import { ProspeccionFilterProvider } from "./context/ProspeccionFilterContext";
import { MapaBuscadorProvider } from "./context/MapaBuscadorContext";
import { CotizadorPrefillProvider } from "./context/CotizadorContext";
import Home from "./screens/Home";
import HomeAdmin from "./screens/admin/HomeAdmin";
import HomeSuperAdmin from "./screens/admin/HomeSuperAdmin";
import Login from "./screens/Login";
import Incidencias from "./screens/Incidencias";
import Novedades from "./screens/News";
import Proveedores from "./screens/Proveedores";
import Amenities from "./screens/Amenities";
import FloatingActionButton from "./components/FloatingActionButton";
import Cuentas from "./screens/Cuentas";
import Expensas from "./screens/Expensas";
import MisPagos from "./screens/MisPagos";
import Documentos from "./screens/Documentos";
import AsambleasResidente from "./screens/AsambleasResidente";
import ObrasYProyectos from "./screens/ObrasYProyectos";
import Revisiones from "./screens/Revisiones";
import DocumentacionEdificio from "./screens/DocumentacionEdificio";
import Residentes from "./screens/admin/Residentes";
import UnidadDetalle from "./screens/admin/UnidadDetalle";
import PagosUnidad from "./screens/admin/PagosUnidad";
import IncidenciasUnidad from "./screens/admin/IncidenciasUnidad";
import { BuildingProvider, useBuilding } from "./context/BuildingContext";
import { UserProvider, useUser } from "./context/UserContext";
import { getRolesForEmail, resolveActiveRole, setLastRoleDocId, homeForType } from "./utils/roleSwitch";
import { vincularMiCuenta } from "./utils/identityApi";
import VersionBanner from "./components/VersionBanner";
import MisEdificios from "./screens/admin/MisEdificios";
import PagosEdificio from "./screens/admin/PagosEdificio";
import Prospeccion from "./screens/admin/Prospeccion";
import Desarrolladoras from "./screens/comercial/Desarrolladoras";
import Administraciones from "./screens/comercial/Administraciones";
import Materiales from "./screens/comercial/Materiales";
import ContactosComercial from "./screens/comercial/ContactosComercial";
import Cotizador from "./screens/comercial/Cotizador";
import PresupuestoExpress from "./screens/comercial/PresupuestoExpress";
import CotizadorBackoffice from "./screens/comercial/CotizadorBackoffice";
import VerComoResidente from "./screens/admin/VerComoResidente";
import { EffectiveIdentityProvider } from "./context/EffectiveIdentityContext";
import ViewAsResidentBanner from "./components/app/ViewAsResidentBanner";
import IncidenciasEdificio from "./screens/admin/IncidenciasEdificio";
import NovedadesEdificio from "./screens/admin/NovedadesEdificio";
import ProveedoresEdificio from "./screens/admin/ProveedoresEdificio";
import EditarEdificio from "./screens/admin/EditarEdificio";
import GestionAdmins from "./screens/admin/GestionAdmins";
import GestionComerciales from "./screens/admin/GestionComerciales";
import SeguimientoComercial from "./screens/admin/SeguimientoComercial";
import AdminVistaSoporte from "./screens/admin/AdminVistaSoporte";
import CuentasAdmin from "./screens/admin/Cuentas";
import PermisosAdmin from "./screens/PermisosAdmin";
import PermisosUsuario from "./screens/PermisosUsuario";
import CriteriosDivisionGastos from "./screens/admin/CriteriosDivisionGastos";
import CierreMes from "./screens/admin/CierreMes";
import CierreRevision from "./screens/admin/CierreRevision";
import CriteriosPorEdificios from "./screens/admin/CriteriosPorEdificios";
import Habi from "./screens/Habi";
import AsambleasAdmin from "./screens/admin/asambleas/AsambleasAdmin";
import ModoSala from "./screens/admin/asambleas/ModoSala";
import { colors, zIndex } from "./utils/designTokens";
import ProyectosAdmin from "./screens/admin/ProyectosAdmin";
import RevisionesAdmin from "./screens/admin/RevisionesAdmin";
import DocumentacionAdmin from "./screens/admin/DocumentacionAdmin";
import AsistenciaEmpleados from "./screens/admin/AsistenciaEmpleados";
import TurnosAdmin from "./screens/admin/TurnosAdmin";
import ChatAdmin from "./screens/admin/ChatAdmin";
import TareasAdmin from "./screens/admin/TareasAdmin";
import SolicitudesAdmin from "./screens/admin/SolicitudesAdmin";
import SolicitudesEliminacion from "./screens/admin/SolicitudesEliminacion";
import RecibosAdmin from "./screens/admin/RecibosAdmin";
import MultasAdmin from "./screens/admin/MultasAdmin";
import CargosPuntualesAdmin from "./screens/admin/CargosPuntualesAdmin";
import IngresosEdificio from "./screens/admin/IngresosEdificio";
import MultasResidente from "./screens/MultasResidente";
import MisConsultas from "./screens/MisConsultas";
import ConsultasAdmin from "./screens/admin/ConsultasAdmin";
import CobranzaAdmin from "./screens/admin/CobranzaAdmin";
import GastosVecinosAdmin from "./screens/admin/GastosVecinosAdmin";
import EstadoCuentaEdificio from "./screens/admin/EstadoCuentaEdificio";
import InformeGastosComunes from "./screens/admin/InformeGastosComunes";
import Presupuestos from "./screens/admin/Presupuestos";
import Reglamento from "./screens/admin/Reglamento";
import ActaDesdeAudio from "./screens/admin/Asambleas"; // herramienta vieja; la reemplaza la Fase 2 de Asambleas
import ActasEditor from "./screens/admin/ActasEditor";
import EditorReels from "./screens/admin/EditorReels";
import EquipoMarketingAgentico from "./screens/admin/EquipoMarketingAgentico";
import PautaMarketing from "./screens/admin/PautaMarketing";
import ConciliacionBancaria from "./screens/admin/ConciliacionBancaria";
import Mantenimiento from "./screens/admin/Mantenimiento";
import Procesos from "./screens/admin/Procesos";
import KBHabitia from "./screens/admin/KBHabitia";
import ReservasAmenities from "./screens/admin/ReservasAmenities";
import Partners from "./screens/admin/Partners";
import PlantillasWhatsApp from "./screens/admin/PlantillasWhatsApp";
import ConversacionesWhatsApp from "./screens/admin/ConversacionesWhatsApp";
import RevisionBot from "./screens/admin/RevisionBot";
import MonitorAgentesIA from "./screens/admin/MonitorAgentesIA";
import Leads from "./screens/admin/Leads";
import NotificacionesDashboard from "./screens/admin/NotificacionesDashboard";
// Employee screens
import HeaderEmployee from "./components/HeaderEmployee";
import SidebarEmployee from "./components/SidebarEmployee";
import HomeEmployee from "./screens/employee/HomeEmployee";
import ChatEmployee from "./screens/employee/ChatEmployee";
import CheckInOut from "./screens/employee/CheckInOut";
import IncidentesEmployee from "./screens/employee/IncidentesEmployee";
import RecibosEmployee from "./screens/employee/RecibosEmployee";
import Solicitudes from "./screens/employee/Solicitudes";
import TareasEmployee from "./screens/employee/TareasEmployee";
import TurnosEmployee from "./screens/employee/TurnosEmployee";
import MiPerfil from "./screens/MiPerfil";
import Configuracion from "./screens/Configuracion";
import BottomNav from "./components/BottomNav";
import Finanzas from "./screens/Finanzas";
import CuentasEdificio from "./screens/CuentasEdificio";
import GastosVecinos from "./screens/GastosVecinos";
import Edificio from "./screens/Edificio";
import "./i18n";
import { LanguageProvider } from "./context/LanguageContext";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "./hooks/useResponsive";
import RadialMenu from "./components/app/RadialMenu";
import ReadOnlyBanner from "./components/app/ReadOnlyBanner";
import { TutorialProvider, useTutorial } from "./context/TutorialContext";
import TutorialOverlay from "./components/TutorialOverlay";
import { GENERAL_TUTORIAL } from "./data/tutorialSteps";
import { FaHome as FaHomeC, FaBullseye as FaBullseyeC, FaMapMarkedAlt as FaMapC, FaUser as FaUserC, FaRegBuilding as FaDevC, FaCity as FaAdmC, FaRegFileAlt as FaMatC, FaCalculator as FaCalcC, FaRegAddressCard as FaContC } from "react-icons/fa";
import { FaClock as FaClockE, FaTasks as FaTasksE, FaExclamationTriangle as FaAlertE, FaComments as FaChatE } from "react-icons/fa";

// Menú radial del comercial en mobile (mismo patrón que el residente).
const COMERCIAL_MENU = [
  { key: "homecomercial", label: "Inicio", icon: <FaHomeC /> },
  { key: "prospeccion", label: "Lista", icon: <FaBullseyeC /> },
  { key: "mapaprospeccion", label: "Mapa", icon: <FaMapC /> },
  { key: "desarrolladoras", label: "Desarroll.", icon: <FaDevC /> },
  { key: "administraciones", label: "Administr.", icon: <FaAdmC /> },
  { key: "contactoscomercial", label: "Contactos", icon: <FaContC /> },
  { key: "materiales", label: "Materiales", icon: <FaMatC /> },
  { key: "cotizador", label: "Cotizador", icon: <FaCalcC /> },
  { key: "presupuestoexpress", label: "Presup. exprés", icon: <FaCalcC /> },
  { key: "perfil", label: "Perfil", icon: <FaUserC /> },
];

// Menú radial del empleado en mobile (mismo patrón/estilo que el residente).
// El Home ya enlaza al resto (Turnos, Licencias, Liquidaciones); estos 5 son los
// accesos de campo más frecuentes. Perfil/logout van en el header.
const EMPLOYEE_MENU = [
  { key: "homeemployee", label: "Inicio", icon: <FaHomeC /> },
  { key: "checkin", label: "Asistencia", icon: <FaClockE /> },
  { key: "tareasemployee", label: "Tareas", icon: <FaTasksE /> },
  { key: "incidentesemployee", label: "Incidencias", icon: <FaAlertE /> },
  { key: "chatemployee", label: "Chat", icon: <FaChatE /> },
];

const AppContent = ({
  selected,
  setSelected,
  goBack,
  userType,
  onLogout,
  setNavigationGuard,
  userid,
  userName,
  previewAsAdmin = false,
  supportAdminPreview = null,
  exitAdminPreview,
  setSupportAdminPreview,
  previewAsResident = false,
  residentPreview = null,
  exitResidentPreview,
  setResidentPreview,
}) => {
  const { t: tCommon } = useTranslation("common");
  const { selectedBuilding, buildings, selectedUnit } = useBuilding();
  // Residente saliente con acceso de solo-lectura a una unidad histórica:
  // puede ver su histórico y saldar pagos, pero no realizar acciones nuevas.
  const isReadOnlyUnit = userType === 0 && !!selectedUnit?.isReadOnly;
  // Residente en mobile (<768px): shell tipo app con menú radial en vez de sidebar.
  const isMobile = useIsMobile();
  const [unidadSeleccionada, setUnidadSeleccionada] = useState(null);
  // Pantalla a la que vuelve el botón "Atrás" del detalle de unidad, según de
  // dónde se abrió (Editar Edificio vs. grilla de unidades).
  const [unidadBackTo, setUnidadBackTo] = useState("residentes");
  // Pestaña con la que abre Editar Edificio (para volver a "Unidades" desde el
  // detalle de unidad). null = pestaña por defecto.
  const [editarEdificioTab, setEditarEdificioTab] = useState(null);
  const [adminVistaSoporteDocId, setAdminVistaSoporteDocId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('habitia_sidebar_collapsed') === 'true'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('habitia_sidebar_collapsed', sidebarCollapsed); } catch {}
  }, [sidebarCollapsed]);
  const [startVoiceOnIncidencias, setStartVoiceOnIncidencias] = useState(false);

  // Auto-trigger tutorial general para admins en primer ingreso
  const tutorial = useTutorial();
  useEffect(() => {
    if ((userType === 1 || userType === 2) && !previewAsAdmin) {
      // Pequeno delay para que el layout se renderice antes del spotlight
      const timer = setTimeout(() => {
        tutorial.startTutorial(GENERAL_TUTORIAL);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pantallas de Admin que usan SidebarAdmin
  const adminScreens = [
    "perfil",
    "configuracion",
    "homeadmin",
    "edificios",
    "habi",
    "residentes",
    "gestionusuarios",
    "cuentas",
    "cuentasadmin",
    "onboardingresidente",
    "pagosedificio",
    "incidenciasedificio",
    "novedadesedificio",
    "proveedoresedificio",
    "cierredemes",
    "revisioncierres",
    "criteriosporedificios",
    "unidad",
    "pagosunidad",
    "incidenciasunidad",
    "editaredificio",
    "reunionesadmin",
    "proyectosadmin",
    "revisionesadmin",
    "documentacionadmin",
    "asistenciaempleados",
    "turnosadmin",
    "chatadmin",
    "whatsappinbox",
    "consultasadmin",
    "tareasadmin",
    "solicitudesadmin",
    "recibosadmin",
    "multasadmin",
    "cargospuntuales",
    "ingresosedificio",
    "gastosvecinosadmin",
    "cobranzaadmin",
    "estadocuentaedificio",
    "informegastos",
    "reservasamenities",
    "presupuestos",
    "vercomoresidente",
    "reglamento",
    "asambleas",
    "actaaudio",
    "actas",
    "conciliacion",
    "mantenimiento",
  ];

  useEffect(() => {
    if (previewAsAdmin && selected && !adminScreens.includes(selected)) {
      setSelected("homeadmin");
    }
  }, [previewAsAdmin, selected, setSelected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pantallas de Empleado que usan SidebarEmployee
  const employeeScreens = [
    "perfil",
    "configuracion",
    "homeemployee",
    "checkin",
    "tareasemployee",
    "incidentesemployee",
    "chatemployee",
    "solicitudes",
    "recibos",
    "turnos",
  ];

  // Pantallas del usuario Comercial que usan SidebarComercial
  const comercialScreens = [
    "perfil",
    "configuracion",
    "homecomercial",
    "prospeccion",
    "mapaprospeccion",
    "desarrolladoras",
    "administraciones",
    "contactoscomercial",
    "materiales",
    "cotizador",
    "presupuestoexpress",
  ];

  // Pantallas de SuperAdmin que usan SidebarSuperAdmin
  const superAdminScreens = [
    "perfil",
    "configuracion",
    "homesuperadmin",
    "edificios",
    "gestionadmins",
    "adminsoporte",
    "gestionusuarios",
    "cuentas",
    "cuentasadmin",
    "permisosadmin",
    "permisosusuario",
    "editaredificio",
    "habi",
    "solicitudeseliminacion",
    "partners",
    "leads",
    "whatsapptemplates",
    "whatsappinbox",
    "whatsapprevisionbot",
    "whatsappdashboard",
    "monitoragentesia",
    "presupuestos",
    "vercomoresidente",
    "reglamento",
    "asambleas",
    "actaaudio",
    "actas",
    "conciliacion",
    "mantenimiento",
    "procesos",
    "editorreels",
    "equipomarketing",
    "pautamarketing",
    "prospeccion",
    "mapaprospeccion",
    "desarrolladoras",
    "administraciones",
    "materiales",
    "cotizador",
    "presupuestoexpress",
    "cotizadorbackoffice",
    "gestioncomerciales",
    "seguimientocomercial",
    "kbhabitia",
    "cobranzaadmin",
    "multasadmin",
    "cargospuntuales",
    "ingresosedificio",
    "estadocuentaedificio",
  ];

  // Pantallas sin sidebar (residentes sin edificio seleccionado)
  const sinSidebar = [
    "criteriosdivisiongastos",
  ];

  /** En vista previa, las pantallas se comportan según el tipo del target:
   *  "ver como residente" → 0; "vista de soporte" (admin) → 1; si no, el real. */
  const effectiveUserType = previewAsResident ? 0 : (previewAsAdmin ? 1 : userType);

  const renderContent = () => {
    // FIN-020: Guard — residentes no pueden acceder a pantallas admin
    const adminOnlyScreens = [
      "homeadmin", "homesuperadmin", "edificios", "residentes", "gestionusuarios", "cuentasadmin",
      "onboardingresidente", "pagosedificio", "incidenciasedificio", "novedadesedificio",
      "proveedoresedificio", "cierredemes", "revisioncierres", "criteriosdivisiongastos",
      "criteriosporedificios", "reunionesadmin", "proyectosadmin", "revisionesadmin",
      "documentacionadmin", "asistenciaempleados", "turnosadmin", "chatadmin",
      "tareasadmin", "solicitudesadmin", "recibosadmin", "multasadmin", "cargospuntuales", "ingresosedificio", "cobranzaadmin",
      "estadocuentaedificio", "informegastos", "reservasamenities", "centrocomando",
      "gestionadmins", "permisosadmin", "permisosusuario", "amenityconfig",
      "solicitudeseliminacion", "editorreels", "equipomarketing", "pautamarketing", "vercomoresidente",
      "asambleas", "actaaudio", "actas",
      "cotizadorbackoffice",
    ];
    if (adminOnlyScreens.includes(selected) && effectiveUserType === 0) {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", textAlign: "center", padding: 24 }}>
          <div style={{ fontSize: 48, marginBottom: 16, color: "#ef4444" }}>403</div>
          <h2 style={{ fontSize: 18, color: "#1a1a1a", margin: "0 0 8px 0" }}>Acceso restringido</h2>
          <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 24px 0" }}>No tenés permisos para acceder a esta sección.</p>
          <button onClick={() => setSelected("inicio")} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: "#0080ff", color: "#fff", fontSize: 14, cursor: "pointer" }}>Volver al inicio</button>
        </div>
      );
    }

    switch (selected) {
      case "homeadmin":
        return <HomeAdmin setSelected={setSelected} />;
      case "homesuperadmin":
        return <HomeSuperAdmin setSelected={setSelected} />;
      case "inicio":
        return <Home setSelected={setSelected} />;
      case "finanzas":
        return <Finanzas setSelected={setSelected} goBack={goBack} />;
      case "edificio":
        return <Edificio setSelected={setSelected} goBack={goBack} />;
      case "habi":
        return <Habi setSelected={setSelected} goBack={goBack} userType={userType} previewAsAdmin={previewAsAdmin} />;
      case "problema":
        return <Incidencias setSelected={setSelected} goBack={goBack} startVoice={startVoiceOnIncidencias} onVoiceStarted={() => setStartVoiceOnIncidencias(false)} />;
      case "incidencias_mis":
        return <Incidencias setSelected={setSelected} goBack={goBack} initialTab="mis" />;
      case "cuentas":
        return <Cuentas setSelected={setSelected} goBack={goBack} />;
      case "cuentasedificio":
        return <CuentasEdificio setSelected={setSelected} goBack={goBack} />;
      case "gastosvecinos":
        return <GastosVecinos setSelected={setSelected} goBack={goBack} />;
      case "expensas":
        return <Expensas setSelected={setSelected} goBack={goBack} />;
      case "mispagos":
        return <MisPagos setSelected={setSelected} goBack={goBack} />;
      case "documentos":
        return <Documentos setSelected={setSelected} goBack={goBack} />;
      case "multasresidente":
        return <MultasResidente setSelected={setSelected} goBack={goBack} />;
      case "misconsultas":
        return <MisConsultas goBack={goBack} />;
      case "reuniones": // id histórico: ahora es Asambleas (solo propietarios)
        return <AsambleasResidente goBack={goBack} />;
      case "obras":
        return <ObrasYProyectos setSelected={setSelected} goBack={goBack} />;
      case "revisiones":
        return <Revisiones setSelected={setSelected} goBack={goBack} />;
      case "documentacion":
        return <DocumentacionEdificio setSelected={setSelected} goBack={goBack} />;
      case "amenities":
        return <Amenities setSelected={setSelected} goBack={goBack} />;
      case "novedades":
        return <Novedades setSelected={setSelected} goBack={goBack} />;
      case "prestadores":
        return <Proveedores setSelected={setSelected} goBack={goBack} />;
      case "perfil":
        return <MiPerfil setSelected={setSelected} goBack={goBack} />;
      case "configuracion":
        return <Configuracion setSelected={setSelected} goBack={goBack} />;
      case "residentes":
        return (
          <Residentes
            setSelected={setSelected}
            setUnidadSeleccionada={setUnidadSeleccionada}
            setUnidadBackTo={setUnidadBackTo}
          />
        );
      case "unidad":
        return (
          <UnidadDetalle
            unidadId={unidadSeleccionada}
            setSelected={setSelected}
            backTo={unidadBackTo}
            userType={effectiveUserType}
          />
        );
      case "pagosunidad":
        return (
          <PagosUnidad
            unidadId={unidadSeleccionada}
            setSelected={setSelected}
          />
        );
      case "pagosedificio":
        return <PagosEdificio setSelected={setSelected} />;
      case "gastosvecinosadmin":
        return <GastosVecinosAdmin setSelected={setSelected} />;
      case "prospeccion":
        return <Prospeccion setSelected={setSelected} />;
      case "mapaprospeccion":
        return <MapaProspeccion setSelected={setSelected} />;
      case "desarrolladoras":
        return <Desarrolladoras />;
      case "administraciones":
        return <Administraciones setSelected={setSelected} />;
      case "materiales":
        return <Materiales />;
      case "cotizador":
        return <Cotizador />;
      case "presupuestoexpress":
        return <PresupuestoExpress />;
      case "cotizadorbackoffice":
        return <CotizadorBackoffice setSelected={setSelected} />;
      case "incidenciasunidad":
        return (
          <IncidenciasUnidad
            unidadId={unidadSeleccionada}
            setSelected={setSelected}
          />
        );
      case "incidenciasedificio":
        return <IncidenciasEdificio setSelected={setSelected} />;
      case "edificios":
        return <MisEdificios setSelected={setSelected} />;
      case "novedadesedificio":
        return <NovedadesEdificio setSelected={setSelected} />;
      case "proveedoresedificio":
        return <ProveedoresEdificio setSelected={setSelected} />;
      case "editaredificio":
        return <EditarEdificio setSelected={setSelected} setNavigationGuard={setNavigationGuard} setUnidadSeleccionada={setUnidadSeleccionada} setUnidadBackTo={setUnidadBackTo} initialTab={editarEdificioTab} setInitialTab={setEditarEdificioTab} userType={effectiveUserType} />;
      case "solicitudeseliminacion":
        return <SolicitudesEliminacion setSelected={setSelected} />;
      case "partners":
        return <Partners />;
      case "leads":
        return <Leads />;
      case "whatsapptemplates":
        return <PlantillasWhatsApp />;
      case "whatsappinbox":
        return <ConversacionesWhatsApp scope={effectiveUserType >= 2 ? "all" : "building"} />;
      case "consultasadmin":
        return <ConsultasAdmin />;
      case "whatsapprevisionbot":
        return <RevisionBot />;
      case "whatsappdashboard":
        return <NotificacionesDashboard />;
      case "monitoragentesia":
        return <MonitorAgentesIA />;
      case "gestionadmins":
        return (
          <GestionAdmins
            setSelected={setSelected}
            setAdminVistaSoporteDocId={setAdminVistaSoporteDocId}
          />
        );
      case "gestioncomerciales":
        return <GestionComerciales setSelected={setSelected} />;
      case "seguimientocomercial":
        return <SeguimientoComercial setSelected={setSelected} />;
      case "adminsoporte":
        return (
          <AdminVistaSoporte
            setSelected={setSelected}
            adminUserDocId={adminVistaSoporteDocId}
            clearAdminVista={() => setAdminVistaSoporteDocId(null)}
            onEnterPanelPreview={
              setSupportAdminPreview
                ? (payload) => {
                    setSupportAdminPreview(payload);
                    setSelected("homeadmin");
                  }
                : undefined
            }
          />
        );
      case "gestionusuarios":
        // Bloque 5 del rediseño: la pantalla nueva "Residentes" (CuentasAdmin)
        // reemplaza al GestionUsuarios viejo. Mantenemos la key vieja para no
        // romper bookmarks ni links internos.
        return <CuentasAdmin setSelected={setSelected} userType={effectiveUserType} />;
      case "onboardingresidente":
        // Bloque 5: OnboardingResidente reemplazado por la vista unificada
        // CuentasAdmin. Mantenemos la ruta vieja con redirect para no romper
        // bookmarks ni callsites externos.
        return <CuentasAdmin setSelected={setSelected} userType={effectiveUserType} />;
      case "permisosadmin":
        return <PermisosAdmin setSelected={setSelected} />;
      case "permisosusuario":
        return <PermisosUsuario setSelected={setSelected} />;
      case "criteriosdivisiongastos":
        return <CriteriosDivisionGastos setSelected={setSelected} />;
      case "cierredemes":
        return <CierreMes setSelected={setSelected} />;
      case "revisioncierres":
        return <CierreRevision />;
      case "criteriosporedificios":
        return <CriteriosPorEdificios setSelected={setSelected} />;
      case "reunionesadmin": // alias viejo de Reuniones
        return <AsambleasAdmin />;
      case "proyectosadmin":
        return <ProyectosAdmin setSelected={setSelected} />;
      case "revisionesadmin":
        // Revisiones se unificó con Mantenimientos — redirigimos al destino nuevo.
        return <Mantenimiento setSelected={setSelected} />;
      case "documentacionadmin":
        return <DocumentacionAdmin setSelected={setSelected} />;
      case "asistenciaempleados":
        return <AsistenciaEmpleados setSelected={setSelected} />;
      case "turnosadmin":
        return <TurnosAdmin setSelected={setSelected} />;
      case "chatadmin":
        return <ChatAdmin setSelected={setSelected} />;
      case "tareasadmin":
        return <TareasAdmin setSelected={setSelected} />;
      case "solicitudesadmin":
        return <SolicitudesAdmin setSelected={setSelected} />;
      case "recibosadmin":
        return <RecibosAdmin setSelected={setSelected} />;
      case "multasadmin":
        return <MultasAdmin setSelected={setSelected} />;
      case "cargospuntuales":
        return <CargosPuntualesAdmin setSelected={setSelected} />;
      case "ingresosedificio":
        return <IngresosEdificio setSelected={setSelected} />;
      case "cobranzaadmin":
        return <CobranzaAdmin setSelected={setSelected} />;
      case "estadocuentaedificio":
        return <EstadoCuentaEdificio setSelected={setSelected} />;
      case "informegastos":
        return <InformeGastosComunes setSelected={setSelected} />;
      case "presupuestos":
        return <Presupuestos setSelected={setSelected} />;
      case "vercomoresidente":
        return (
          <VerComoResidente
            onEnter={(payload) => {
              setResidentPreview && setResidentPreview(payload);
              setSelected("inicio");
            }}
          />
        );
      case "reglamento":
        return <Reglamento setSelected={setSelected} />;
      case "asambleas":
        return <AsambleasAdmin />;
      case "actaaudio":
        return <ActaDesdeAudio setSelected={setSelected} />;
      case "actas":
        return <ActasEditor setSelected={setSelected} />;
      case "conciliacion":
        return <ConciliacionBancaria setSelected={setSelected} />;
      case "mantenimiento":
        return <Mantenimiento setSelected={setSelected} />;
      case "procesos":
        return <Procesos />;
      case "editorreels":
        return <EditorReels />;
      case "equipomarketing":
        return <EquipoMarketingAgentico />;
      case "pautamarketing":
        return <PautaMarketing />;
      case "kbhabitia":
        return <KBHabitia setSelected={setSelected} />;
      case "reservasamenities":
        return <ReservasAmenities setSelected={setSelected} />;
      // Employee screens
      case "homeemployee":
        return <HomeEmployee setSelected={setSelected} />;
      case "chatemployee":
        return <ChatEmployee setSelected={setSelected} />;
      case "checkin":
        return <CheckInOut setSelected={setSelected} />;
      case "tareasemployee":
        return <TareasEmployee setSelected={setSelected} />;
      case "incidentesemployee":
        return <IncidentesEmployee setSelected={setSelected} />;
      case "recibos":
        return <RecibosEmployee setSelected={setSelected} />;
      case "solicitudes":
        return <Solicitudes setSelected={setSelected} />;
      case "turnos":
        return <TurnosEmployee setSelected={setSelected} />;
      default:
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", textAlign: "center", padding: 24 }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>404</div>
            <h2 style={{ fontSize: 20, color: "#1a1a1a", margin: "0 0 8px 0" }}>Página no encontrada</h2>
            <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 24px 0" }}>La sección que buscas no existe o fue movida.</p>
            <button onClick={() => setSelected(effectiveUserType === 2 ? "homesuperadmin" : effectiveUserType === 1 ? "homeadmin" : effectiveUserType === 3 ? "homeemployee" : "inicio")} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: "#0080ff", color: "#fff", fontSize: 14, cursor: "pointer" }}>Volver al inicio</button>
          </div>
        );
    }
  };

  // ⬇️ No bloquees para admins/superadmins ni para "inicio" de residentes
  if (!selectedBuilding && userType === 0 && selected !== "inicio" && selected !== "perfil" && selected !== "configuracion") {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f7fa",
        }}
      >
        <div style={{
          width: "50px",
          height: "50px",
          border: "4px solid #e5e7eb",
          borderTop: "4px solid #0080ff",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
        }}></div>
      </div>
    );
  }

  // Layout para SuperAdmins con SidebarSuperAdmin (sidebar fijo, scroll solo en contenido)
  if (userType === 2 && !previewAsAdmin && superAdminScreens.includes(selected)) {
    return (
      <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
        <SidebarSuperAdmin
          selected={selected}
          setSelected={setSelected}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
        />
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0, marginLeft: sidebarCollapsed ? 60 : 240, transition: "margin-left 0.2s ease" }}>
          <HeaderSuperAdmin selected={selected} setSelected={setSelected} onLogout={onLogout} />
          <main style={{ flex: 1, background: "#f5f7fa", overflowY: "auto", minHeight: 0 }}>
            {renderContent()}
          </main>
        </div>
      </div>
    );
  }

  // Layout para Admins con SidebarAdmin (sidebar fijo, scroll solo en contenido)
  // previewAsAdmin: superadmin viendo el panel como otro admin (BuildingProvider anidado en MainApp)
  if ((userType === 1 || previewAsAdmin) && !previewAsResident && adminScreens.includes(selected)) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
        {previewAsAdmin && supportAdminPreview && (
          <div
            style={{
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "10px 16px",
              background: "linear-gradient(90deg, #1e3a5f 0%, #0f172a 100%)",
              color: "#f8fafc",
              fontSize: 14,
              borderBottom: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <span>
              Vista de soporte: estás viendo Habitia como{" "}
              <strong>{supportAdminPreview.name || supportAdminPreview.email}</strong> (solo para ayuda; no es su sesión).
            </span>
            <button
              type="button"
              onClick={() => exitAdminPreview && exitAdminPreview()}
              style={{
                flexShrink: 0,
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.35)",
                background: "rgba(255,255,255,0.1)",
                color: "#fff",
                cursor: "pointer",
                fontSize: 13,
                fontWeight: 400,
              }}
            >
              Salir de la vista
            </button>
          </div>
        )}
        <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
          <SidebarAdmin
            selected={selected}
            setSelected={setSelected}
            collapsed={sidebarCollapsed}
            setCollapsed={setSidebarCollapsed}
            selectedBuilding={selectedBuilding}
          />
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0, marginLeft: sidebarCollapsed ? 60 : 220, transition: "margin-left 0.2s ease" }}>
            <HeaderAdmin selected={selected} setSelected={setSelected} userType={previewAsAdmin ? 1 : userType} onLogout={onLogout} />
            <main style={{ flex: 1, background: "#f5f7fa", overflowY: "auto", minHeight: 0 }}>
              {renderContent()}
            </main>
          </div>
        </div>
      </div>
    );
  }

  // Layout para Empleados con SidebarEmployee (sidebar azul fijo)
  // Si es empleado pero landed en una pantalla no-employee (ej: "inicio"), redirigir a homeemployee
  if (userType === 3) {
    const effectiveSelected = employeeScreens.includes(selected) ? selected : "homeemployee";
    if (effectiveSelected !== selected) {
      setTimeout(() => setSelected("homeemployee"), 0);
    }
    const employeeContent = (() => {
      switch (effectiveSelected) {
        case "homeemployee": return <HomeEmployee setSelected={setSelected} userid={userid} />;
        case "checkin": return <CheckInOut userid={userid} />;
        case "chatemployee": return <ChatEmployee userid={userid} userName={userName} />;
        case "solicitudes": return <Solicitudes userid={userid} userName={userName} />;
        case "recibos": return <RecibosEmployee userid={userid} />;
        case "tareasemployee": return <TareasEmployee userid={userid} />;
        case "turnos": return <TurnosEmployee userid={userid} />;
        case "incidentesemployee": return <IncidentesEmployee userid={userid} userName={userName} />;
        case "perfil": return <MiPerfil setSelected={setSelected} />;
        case "configuracion": return <Configuracion setSelected={setSelected} />;
        default: return <HomeEmployee setSelected={setSelected} userid={userid} />;
      }
    })();

    // Mobile: shell full-screen tipo app + menú radial (mismo estilo que residente/comercial).
    if (isMobile) {
      return (
        <div style={{ display: "flex", flexDirection: "column", height: "100dvh", overflow: "hidden", background: "#f5f7fa" }}>
          <HeaderEmployee setSelected={setSelected} onLogout={onLogout} mobile />
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch", paddingBottom: "calc(96px + env(safe-area-inset-bottom, 0px))" }}>
            {employeeContent}
          </div>
          <RadialMenu items={EMPLOYEE_MENU} onNavigate={setSelected} />
        </div>
      );
    }

    return (
      <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
        <SidebarEmployee
          selected={effectiveSelected}
          setSelected={setSelected}
          collapsed={sidebarCollapsed}
          setCollapsed={setSidebarCollapsed}
          userid={userid}
        />
        <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0, marginLeft: sidebarCollapsed ? 64 : 220, transition: "margin-left 0.2s ease" }}>
          <HeaderEmployee setSelected={setSelected} onLogout={onLogout} />
          <main style={{ flex: 1, background: "#f5f7fa", overflowY: "auto", minHeight: 0 }}>
            {employeeContent}
          </main>
        </div>
      </div>
    );
  }

  // Layout para usuario Comercial (typeuser 4): SidebarComercial en desktop,
  // shell full-screen tipo app + BottomNavComercial en mobile (uso en la calle).
  if (userType === 4) {
    const s = comercialScreens.includes(selected) ? selected : "homecomercial";
    if (s !== selected) {
      setTimeout(() => setSelected("homecomercial"), 0);
    }
    const comercialContent = (() => {
      switch (s) {
        case "homecomercial": return <HomeComercial setSelected={setSelected} userid={userid} />;
        case "prospeccion": return <Prospeccion setSelected={setSelected} />;
        case "mapaprospeccion": return <MapaProspeccion setSelected={setSelected} />;
        case "desarrolladoras": return <Desarrolladoras />;
        case "administraciones": return <Administraciones setSelected={setSelected} />;
        case "contactoscomercial": return <ContactosComercial setSelected={setSelected} />;
        case "materiales": return <Materiales />;
        case "cotizador": return <Cotizador />;
        case "presupuestoexpress": return <PresupuestoExpress />;
        case "perfil": return <MiPerfil setSelected={setSelected} />;
        case "configuracion": return <Configuracion setSelected={setSelected} />;
        default: return <HomeComercial setSelected={setSelected} userid={userid} />;
      }
    })();

    if (isMobile) {
      return (
        <MapaBuscadorProvider>
          <div style={{ display: "flex", flexDirection: "column", height: "100dvh", overflow: "hidden", background: "#f5f7fa" }}>
            <HeaderComercial setSelected={setSelected} onLogout={onLogout} mobile />
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))" }}>
              {comercialContent}
            </div>
            <RadialMenu items={COMERCIAL_MENU} onNavigate={setSelected} />
          </div>
        </MapaBuscadorProvider>
      );
    }

    return (
      <MapaBuscadorProvider>
        <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
          <SidebarComercial
            selected={s}
            setSelected={setSelected}
            collapsed={sidebarCollapsed}
            setCollapsed={setSidebarCollapsed}
          />
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0, marginLeft: sidebarCollapsed ? 64 : 220, transition: "margin-left 0.2s ease" }}>
            <HeaderComercial setSelected={setSelected} onLogout={onLogout} />
            <main style={{ flex: 1, background: "#f5f7fa", overflowY: "auto", minHeight: 0 }}>
              {comercialContent}
            </main>
          </div>
        </div>
      </MapaBuscadorProvider>
    );
  }

  // Shell mobile tipo app para residentes (<768px): menú radial en vez de sidebar/bottomnav.
  if (effectiveUserType === 0 && isMobile && !sinSidebar.includes(selected)) {
    return (
      <div
        className="residentApp"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100dvh",
          overflow: "hidden",
          background: "var(--app-page)",
        }}
      >
        <DemoBanner />
        {previewAsResident && residentPreview && <ViewAsResidentBanner target={residentPreview} onExit={exitResidentPreview} />}
        {isReadOnlyUnit && <ReadOnlyBanner unit={selectedUnit} />}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            WebkitOverflowScrolling: "touch",
            paddingBottom: "calc(96px + env(safe-area-inset-bottom, 0px))",
          }}
        >
          {renderContent()}
        </div>
        <RadialMenu onNavigate={(key) => setSelected(key)} />
      </div>
    );
  }

  // Layout estándar para residentes (sidebar full height, fijo al hacer scroll)
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      {/* Banner de modo demo (solo visible si el user tiene custom claim habitia_demo) */}
      <DemoBanner />
      {previewAsResident && residentPreview && <ViewAsResidentBanner target={residentPreview} onExit={exitResidentPreview} />}
      {/* Header según tipo de usuario */}
      {sinSidebar.includes(selected) ? (
        <HeaderAdmin selected={selected} setSelected={setSelected} userType={userType} onLogout={onLogout} />
      ) : (
        <Header
          setSelected={setSelected} 
          sidebarCollapsed={sidebarCollapsed}
          setSidebarCollapsed={setSidebarCollapsed}
          userType={userType}
          onLogout={onLogout}
        />
      )}

      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {!sinSidebar.includes(selected) && effectiveUserType === 0 && (
          <Sidebar
            selected={selected} 
            setSelected={setSelected} 
            collapsed={sidebarCollapsed}
            setCollapsed={setSidebarCollapsed}
          />
        )}

        <main
          style={{
            flex: 1,
            padding: 0,
            background: "#f5f7fa",
            position: "relative",
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          {isReadOnlyUnit && <ReadOnlyBanner unit={selectedUnit} />}
          {renderContent()}
          {!sinSidebar.includes(selected) && !isReadOnlyUnit && !previewAsResident && (
            <FloatingActionButton
              onClick={() => {
                setSelected("problema");
                setStartVoiceOnIncidencias(true);
              }}
              title={tCommon("reportProblem")}
            />
          )}
        </main>
      </div>
      {effectiveUserType === 0 && (
        <BottomNav selected={selected} setSelected={setSelected} />
      )}
    </div>
  );
};

const MainApp = () => {
  const { setUserData } = useUser();
  const [loggedIn, setLoggedIn] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  // Sube cuando vincularMiCuenta cambia vínculos o permisos: relee los edificios.
  const [accessVersion, setAccessVersion] = useState(0);

  const doLogout = async (reason) => {
    if (reason === "timeout") {
      sessionStorage.setItem("habitia_session_expired", "true");
    }
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Error al cerrar sesión:", err);
    } finally {
      setLoggedIn(false);
      setUserEmail("");
      setUserUserid("");
      setUserName("");
      setUserType(null);
      setUserData({ userid: "", userName: "", userType: null, userEmail: "" });
    }
  };

  const handleManualLogout = useCallback(async () => {
    setSupportAdminPreview(null);
    if (navigationGuardRef.current) {
      const canNavigate = await navigationGuardRef.current();
      if (!canNavigate) return;
    }
    doLogout();
  }, []);

  const handleTimeoutLogout = useCallback(() => {
    doLogout("timeout");
  }, []);
  useSessionTimeout(handleTimeoutLogout, 30 * 60 * 1000);

  const [userEmail, setUserEmail] = useState("");
  const [userUserid, setUserUserid] = useState("");
  const [userName, setUserName] = useState("");
  const [selected, setSelectedRaw] = useState("inicio");
  const navigationGuardRef = useRef(null);
  // Back-stack liviano para el botón "atrás" del shell mobile (nav es switch plano).
  const navHistoryRef = useRef([]);
  const currentSelectedRef = useRef("inicio");
  useEffect(() => {
    currentSelectedRef.current = selected;
  }, [selected]);

  const setSelected = useCallback(async (newScreen) => {
    if (navigationGuardRef.current) {
      const canNavigate = await navigationGuardRef.current();
      if (!canNavigate) return;
    }
    if (currentSelectedRef.current !== newScreen) {
      navHistoryRef.current.push(currentSelectedRef.current);
    }
    setSelectedRaw(newScreen);
  }, []);

  const goBack = useCallback(() => {
    const h = navHistoryRef.current;
    const target = h.length > 0 ? h.pop() : "inicio";
    setSelectedRaw(target);
  }, []);

  // Expose guard setter for children
  const setNavigationGuard = useCallback((guardFn) => {
    navigationGuardRef.current = guardFn;
  }, []);
  const [userType, setUserType] = useState(null);
  /** Superadmin: vista previa del panel como otro admin (soporte) */
  const [supportAdminPreview, setSupportAdminPreview] = useState(null);

  const exitAdminPreview = useCallback(() => {
    setSupportAdminPreview(null);
    setSelectedRaw("gestionadmins");
  }, []);

  /** Admin/superadmin: "ver como residente" (solo lectura, sin cambiar de sesión) */
  const [residentPreview, setResidentPreview] = useState(null);
  const exitResidentPreview = useCallback(() => {
    setResidentPreview(null);
    setSelectedRaw("vercomoresidente");
  }, []);

  // ── Multi-rol: un mismo email puede tener varios docs en `users` (colaborador,
  // comercial, admin, residente). Resolvemos el rol activo (última plataforma
  // abierta, guardada en localStorage) y exponemos el switch al perfil/menú. ──
  const userEmailRef = useRef("");
  const availableRolesRef = useRef([]);
  useEffect(() => { userEmailRef.current = userEmail; }, [userEmail]);

  const switchRole = useCallback((role) => {
    if (!role) return;
    const email = userEmailRef.current;
    setLastRoleDocId(email, role.docId);
    setSupportAdminPreview(null);
    setUserUserid(String(role.userid || ""));
    setUserName(role.name || "");
    setUserType(role.typeuser);
    setUserData({
      userid: String(role.userid || ""),
      userName: role.name || "",
      userType: role.typeuser,
      userEmail: email,
      availableRoles: availableRolesRef.current,
      switchRole,
    });
    setSelected(homeForType(role.typeuser));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setUserData, setSelected]);

  // Carga todos los roles del email, activa el que corresponda y deja la sesión lista.
  const applyRolesForEmail = useCallback(async (email) => {
    const roles = await getRolesForEmail(email);
    if (!roles.length) return false;
    availableRolesRef.current = roles;
    const active = resolveActiveRole(email, roles);
    setUserEmail(email);
    setUserUserid(String(active.userid || ""));
    setUserName(active.name || "");
    setUserType(active.typeuser);
    setUserData({
      userid: String(active.userid || ""),
      userName: active.name || "",
      userType: active.typeuser,
      userEmail: email,
      availableRoles: roles,
      switchRole,
    });
    setLoggedIn(true);
    // Fase 0C: el servidor vincula la cuenta a sus docs (authUid), marca
    // activados los que quedaron "Invitada"/"Vencida" pese a que ya puede
    // loguearse y recalcula sus permisos (user_access). Antes lo escribía el
    // navegador. No bloquea ni rompe la sesión: si vinculó docs o cambiaron
    // los permisos, se releen los roles y los edificios (con las reglas 0D lo
    // legible sale de user_access y las primeras lecturas pudieron salir con
    // el user_access viejo).
    vincularMiCuenta()
      .then((r) => {
        if (!r || !(r.vinculados > 0 || r.changed)) return;
        setAccessVersion((v) => v + 1);
        getRolesForEmail(email)
          .then((nuevos) => {
            if (!nuevos.length || userEmailRef.current !== email) return;
            availableRolesRef.current = nuevos;
            setUserData({ availableRoles: nuevos });
          })
          .catch(() => {});
      })
      .catch(() => {});
    setSelectedRaw(homeForType(active.typeuser));
    return true;
  }, [setUserData, switchRole]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        try {
          await applyRolesForEmail(fbUser.email);
        } catch (err) {
          console.error("Error restaurando sesión:", err);
        }
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (authLoading)
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", backgroundColor: "#f5f7fa" }}>
        <div style={{ width: "50px", height: "50px", border: "4px solid #e5e7eb", borderTop: "4px solid #0080ff", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
      </div>
    );

  if (!loggedIn)
    return (
      <Login
        onLogin={async (session) => {
          sessionStorage.removeItem("habitia_last_activity");
          // Resuelve TODOS los roles del email y activa la última plataforma usada.
          await applyRolesForEmail(session.email);
        }}
      />
    );

  const appContentEl = (
    <React.Suspense
      fallback={
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            backgroundColor: "#f5f7fa",
          }}
        >
          <div style={{
            width: "50px",
            height: "50px",
            border: "4px solid #e5e7eb",
            borderTop: "4px solid #0080ff",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}></div>
        </div>
      }
    >
      <AppContent
        selected={selected}
        setSelected={setSelected}
        goBack={goBack}
        userType={userType}
        onLogout={handleManualLogout}
        setNavigationGuard={setNavigationGuard}
        userid={userUserid}
        userName={userName}
        previewAsAdmin={!!supportAdminPreview}
        supportAdminPreview={supportAdminPreview}
        exitAdminPreview={exitAdminPreview}
        setSupportAdminPreview={setSupportAdminPreview}
        previewAsResident={!!residentPreview}
        residentPreview={residentPreview}
        exitResidentPreview={exitResidentPreview}
        setResidentPreview={setResidentPreview}
      />
    </React.Suspense>
  );

  return (
    <LanguageProvider userEmail={userEmail}>
    <TutorialProvider>
      <BuildingProvider userEmail={userEmail} userid={userUserid} userType={userType} accessVersion={accessVersion}>
        {residentPreview ? (
          <BuildingProvider
            userEmail={residentPreview.email}
            userid={String(residentPreview.userid)}
            userType={0}
          >
            <EffectiveIdentityProvider
              value={{
                email: residentPreview.email,
                userid: residentPreview.userid,
                readOnly: true,
                viewingAs: { name: residentPreview.name, unitLabel: residentPreview.unitLabel },
              }}
            >
              {appContentEl}
            </EffectiveIdentityProvider>
          </BuildingProvider>
        ) : supportAdminPreview ? (
          <BuildingProvider
            userEmail={supportAdminPreview.email}
            userid={String(supportAdminPreview.userid)}
            userType={1}
          >
            {appContentEl}
          </BuildingProvider>
        ) : (
          appContentEl
        )}
      </BuildingProvider>
      <TutorialOverlay />
    </TutorialProvider>
    </LanguageProvider>
  );
};

// ── Modo Sala (Asambleas, Fase 4) ────────────────────────────────────────────
// /sala/:aid: pantalla completa FUERA del layout admin (que no tiene versión
// mobile: el Modo Sala se usa de pie, en un celular). Exige sesión: sin sesión
// muestra el login y, al entrar, el Modo Sala. Los permisos los decide el
// servidor (staff del edificio con user_access): la pantalla muestra su error.
//
// Sesión durante la asamblea: mientras se graba, se sube o la asamblea está en
// curso (ModoSala avisa con onOcupado) no se cierra la sesión por inactividad
// (con el celular quieto sobre la mesa, cortaría la grabación) y se renueva la
// marca de actividad que mira el panel al volver. Fuera de eso rige la misma
// regla del panel (30 minutos sin tocar la pantalla). Si la sesión de Firebase
// se cae igual (otra pestaña cerró la sesión), el login se muestra ENCIMA del
// Modo Sala, sin desmontarlo: la grabación sigue en memoria y las subidas se
// reintentan solas al volver a entrar.
const MS_INACTIVIDAD_SALA = 30 * 60 * 1000;
const CLAVE_ACTIVIDAD = "habitia_last_activity"; // la de useSessionTimeout

const CierrePorInactividad = ({ onTimeout }) => {
  useSessionTimeout(onTimeout, MS_INACTIVIDAD_SALA);
  return null;
};

const pantallaCargando = (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", backgroundColor: colors.bgMain }}>
    <div
      style={{
        width: 48,
        height: 48,
        border: `4px solid ${colors.gray200}`,
        borderTop: `4px solid ${colors.primary}`,
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}
    ></div>
  </div>
);

const RutaModoSala = () => {
  const { aid } = useParams();
  const navigate = useNavigate();
  // cargando → login (no había sesión) → lista (Modo Sala montado)
  const [fase, setFase] = useState("cargando");
  const faseRef = useRef("cargando");
  const [email, setEmail] = useState("");
  const [sinSesion, setSinSesion] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  useEffect(
    () =>
      onAuthStateChanged(auth, (u) => {
        if (faseRef.current === "cargando") {
          faseRef.current = u ? "lista" : "login";
          setFase(faseRef.current);
          if (u) setEmail(u.email || "");
          return;
        }
        // Con el Modo Sala montado, la caída de la sesión no lo desmonta.
        if (faseRef.current === "lista") {
          setSinSesion(!u);
          if (u) setEmail(u.email || "");
        }
      }),
    []
  );

  useEffect(() => {
    if (!ocupado) return undefined;
    const marcar = () => {
      try {
        sessionStorage.setItem(CLAVE_ACTIVIDAD, String(Date.now()));
      } catch (_) {
        /* sin sessionStorage: el panel pedirá entrar de nuevo */
      }
    };
    marcar();
    const t = setInterval(marcar, 60 * 1000);
    return () => clearInterval(t);
  }, [ocupado]);

  const cerrarPorInactividad = useCallback(() => {
    try {
      sessionStorage.setItem("habitia_session_expired", "true");
    } catch (_) {
      /* nada */
    }
    signOut(auth).catch(() => {});
  }, []);

  const alEntrar = useCallback((session) => {
    try {
      sessionStorage.removeItem(CLAVE_ACTIVIDAD);
    } catch (_) {
      /* nada */
    }
    if (session && session.email) setEmail(session.email);
    setSinSesion(false);
    faseRef.current = "lista";
    setFase("lista");
  }, []);

  const volverAlPanel = useCallback(() => navigate("/"), [navigate]);

  if (fase === "cargando") return pantallaCargando;
  if (fase === "login") return <Login onLogin={alEntrar} />;
  return (
    <LanguageProvider userEmail={email}>
      <ModoSala aid={aid} onSalir={volverAlPanel} onOcupado={setOcupado} />
      {!ocupado && !sinSesion && <CierrePorInactividad onTimeout={cerrarPorInactividad} />}
      {sinSesion && (
        <div style={{ position: "fixed", inset: 0, zIndex: zIndex.toast + 1, overflowY: "auto", background: colors.bgMain }}>
          <Login onLogin={alEntrar} />
        </div>
      )}
    </LanguageProvider>
  );
};

// El aviso de versión nueva puede recargar la página solo al volver a la
// pestaña (version.json con forzar): en el Modo Sala cortaría la grabación.
// Ahí no se muestra (el Modo Sala no escribe Firestore directo, solo usa
// callables); al volver al panel vuelve a chequear y recarga si corresponde.
const BannerDeVersion = () => {
  const { pathname } = useLocation();
  return pathname.startsWith("/sala/") ? null : <VersionBanner />;
};

const App = () => (
  <ErrorBoundary>
    <ToastProvider>
      <DialogProvider>
        <UserProvider>
          <ProspeccionFilterProvider>
            <CotizadorPrefillProvider>
              <Routes>
                <Route path="/activar/expirado" element={<ActivacionExpirada />} />
                <Route path="/activar/:token" element={<ActivarCuenta />} />
                <Route path="/cotizacion-pdf" element={<CotizacionPrint />} />
                <Route path="/solicitar-demo" element={<SolicitarDemo />} />
                <Route path="/asamblea/:t" element={<AsambleaPublica />} />
                <Route path="/z/:t" element={<EntrarAsamblea />} />
                <Route path="/sala/:aid" element={<RutaModoSala />} />
                <Route path="/*" element={<MainApp />} />
              </Routes>
              <BannerDeVersion />
            </CotizadorPrefillProvider>
          </ProspeccionFilterProvider>
        </UserProvider>
      </DialogProvider>
    </ToastProvider>
  </ErrorBoundary>
);

export default App;
